import { memo, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '../Tooltip.jsx';

const triggerClassName = 'flex min-w-0 w-full flex-1 overflow-hidden';
const labelClassName = 'min-w-0 flex-1 w-full truncate';

/**
 * Single-line ellipsis target for {@link DataTable} cells with `column.truncate`.
 * Shows a tooltip only when the text overflows (ellipsis visible). Use inside custom
 * `column.render` when the cell has a leading icon or other chrome; pass `tooltipText`
 * with the full string (usually the same as `children`).
 *
 * @param {object} props
 * @param {import('react').ReactNode} props.children
 * @param {string} [props.className]
 * @param {string} [props.tooltipText] — full text for the hover panel; tooltip mounts only on overflow
 */
const DataTableTruncatedText = ({ children, className = '', tooltipText }) => {
  const ref = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const syncOverflow = () => {
      const overflowing = el.scrollWidth > el.clientWidth;
      setIsOverflowing((prev) => (prev === overflowing ? prev : overflowing));
    };

    const scheduleSync = () => requestAnimationFrame(syncOverflow);

    scheduleSync();
    const ro = new ResizeObserver(scheduleSync);
    ro.observe(el);

    let ancestor = el.parentElement;
    while (ancestor) {
      ro.observe(ancestor);
      if (ancestor.tagName === 'TD') break;
      ancestor = ancestor.parentElement;
    }

    return () => ro.disconnect();
  }, [children]);

  const label = (
    <div
      ref={ref}
      className={[labelClassName, className].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );

  if (!tooltipText) return label;

  return (
    <Tooltip
      content={tooltipText}
      position="bottom"
      arrow={false}
      align="start"
      disabled={!isOverflowing}
      triggerClassName={triggerClassName}
    >
      {label}
    </Tooltip>
  );
};

DataTableTruncatedText.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  tooltipText: PropTypes.string,
};

export default memo(DataTableTruncatedText);
